/*
 * The tutorial's overlay graphics: pointer lines, the pole marker, the altitude
 * arc.
 *
 * These are computed from the same sky the lesson is showing, not drawn at fixed
 * screen positions. The pointer line really is the line through Merak and Dubhe
 * extended, and the altitude arc really does read the angle Polaris stands at,
 * so if the astronomy were wrong the guides would visibly disagree with the
 * stars rather than quietly agreeing with a lie.
 */

import { altAzToVector, projectVector } from '../skyRender.mjs';
import { equatorialToHorizontal, julianDate, lstDegrees, normalizeDeg } from '../astronomy.mjs';

const ACCENT = 'rgba(127, 178, 255, 0.95)';
const FAINT = 'rgba(127, 178, 255, 0.35)';
const WARM = 'rgba(255, 209, 128, 0.95)';

// J2000 positions of the stars the guides are built from.
const STARS = {
  polaris: { ra: 37.9529, dec: 89.2641 },
  dubhe: { ra: 165.932, dec: 61.751 },
  merak: { ra: 165.46, dec: 56.382 },
  acrux: { ra: 186.65, dec: -63.099 },
  gacrux: { ra: 187.791, dec: -57.113 },
  /*
   * The far end of the clock hand, one per hemisphere.
   *
   * Kochab is the outer star of the Little Dipper's bowl, and it is what a
   * nocturnal was read against for four centuries, for the same reasons it works
   * here: bright enough to pick out, and far enough off the pole — sixteen
   * degrees — to swing a long arm. Ursa Minor's handle ends on Polaris, so the
   * whole constellation reads as an arm on a pivot rather than as a star that
   * happens to be nearby.
   *
   * Gacrux is the top of the Southern Cross, thirty-three degrees off the south
   * pole, and the Cross's long axis is already the thing the southern lessons
   * teach you to find.
   */
  kochab: { ra: 222.6764, dec: 74.1555 }
};

function place(star, state) {
  const jd = julianDate(state.date);
  return equatorialToHorizontal(star.ra, star.dec, state.lat, lstDegrees(jd, state.lon));
}

const toScreen = (altAz, camera) => projectVector(altAzToVector(altAz.alt, altAz.az), camera);
const toVector = (star, state) => {
  const at = place(star, state);
  return altAzToVector(at.alt, at.az);
};

/*
 * The pole the observer can actually see: due north in the north, due south in
 * the south, standing as many degrees up as the latitude.
 */
const poleAt = (state) => ({ alt: Math.abs(state.lat), az: state.lat >= 0 ? 0 : 180 });

// Whichever hand is above the horizon where the observer is standing.
const handStar = (state) => (state.lat >= 0 ? STARS.kochab : STARS.gacrux);

/*
 * A point on the hand's dial: `hourAngle` degrees round from the top, at a
 * fraction of the hand's own length out from the pole.
 *
 * Hour angle zero is upper culmination, which puts the point directly above the
 * pole in either hemisphere, so the top of the dial means the same thing north
 * and south. Sliding the declination toward the pole walks in along the same
 * hour circle, which is what draws the tick marks.
 */
function dialPoint(star, state, hourAngleDeg, fraction = 1) {
  const distance = (90 - Math.abs(star.dec)) * fraction;
  const dec = state.lat >= 0 ? 90 - distance : distance - 90;
  return equatorialToHorizontal(star.ra, dec, state.lat, star.ra + hourAngleDeg);
}

/*
 * The dial is set for one night, and the drift lesson is about what that costs.
 *
 * What a star clock reads is hour angle — local sidereal time, less the hand
 * star's right ascension. Sidereal time runs about four minutes a day fast on
 * the solar clock, so a dial that is right tonight is two hours out in a month.
 * Left unset the hand printed a bare sidereal number that meant nothing to a
 * player: five in the morning read 19.2h.
 *
 * So the dial is turned once, to read true local time at midnight on the
 * solstice night the star-clock lesson is set on. On that night every mark means
 * the hour it says. The drift lesson immediately after is the honest account of
 * the rest of the year — the same dial runs about two hours a month out, and
 * putting it back is the player's job.
 *
 * Re-setting the dial from the date instead would make every reading true, and
 * is exactly what a real nocturnal's date ring does. It is not done here because
 * the game draws no dial at all: that correction is work the player has to do in
 * their head, and a tutorial that quietly did it for them would be teaching a
 * step the game then withholds.
 */
const DIAL_EPOCH = new Date('2026-12-22T00:00:00Z');

/*
 * How far the dial is turned, in hours, before the hand's own right ascension is
 * added back. Reading and tick both carry the same turn, so the two cannot drift
 * apart; and because the star's RA appears in the hour angle and again here with
 * the opposite sign, it cancels — the reading comes out the same number north
 * and south, off two different hands, which is what makes it a clock rather than
 * a property of Kochab.
 */
const DIAL_TURN =
  ((DIAL_EPOCH.getUTCHours() +
    DIAL_EPOCH.getUTCMinutes() / 60 -
    lstDegrees(julianDate(DIAL_EPOCH), 0) / 15) %
    24 +
    24) %
  24;

const dialSetting = (star) => (DIAL_TURN + star.ra / 15) % 24;

/*
 * Where the dial's `hour` mark sits, as an hour angle. Exported because the test
 * checks the drawn dial against the printed reading: they have to be the same
 * turn, and nothing inside this file would notice if they stopped being.
 */
export function dialMarkHourAngle(state, hour) {
  return normalizeDeg((hour - dialSetting(handStar(state))) * 15);
}

/*
 * What the hand reads, in hours on the set dial. Exported because the lesson
 * captions print it: the number in the caption and the angle on screen have to
 * come from the same place, or the tutorial is quoting a sky it is not drawing.
 */
export function starClockReading(state) {
  const star = handStar(state);
  const lst = lstDegrees(julianDate(state.date), state.lon);
  return (normalizeDeg(lst - star.ra) / 15 + dialSetting(star)) % 24;
}

/*
 * The reading as the captions print it. Rounding has to happen before the wrap,
 * not after: a hand a few thousandths short of a full turn is at 23.9996h, which
 * `toFixed(1)` renders as "24.0h" on a dial whose marks stop at 23. Midnight on
 * the night the dial is set for is exactly that case, so the one instant the
 * clock is most exactly right was the one that looked broken.
 */
export function starClockLabel(state) {
  return ((Math.round(starClockReading(state) * 10) % 240) / 10).toFixed(1);
}

function dashedLine(ctx, from, to, dpr, color = FAINT) {
  ctx.save();
  ctx.setLineDash([6 * dpr, 6 * dpr]);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5 * dpr;
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
  ctx.restore();
}

function ring(ctx, at, radius, dpr, color = ACCENT) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2 * dpr;
  ctx.beginPath();
  ctx.arc(at.x, at.y, radius * dpr, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/*
 * The inset map is opaque and sits over the top right corner of the sky, so a
 * guide label drawn rightward from a mark high in the frame does not overlap it
 * so much as vanish into it — which reads as the guide having been switched off
 * rather than as two things sharing a corner. Its box is fixed by the
 * stylesheet: 300 CSS pixels wide, 14 in from the top and right edges, and about
 * 181 tall once the caption row under it is counted.
 */
const MINIMAP = { width: 300, height: 181, inset: 14 };

/*
 * Where the map actually is, in canvas pixels.
 *
 * Measured off the element rather than taken from the constant above, because
 * the constant is only true at one screen size: on a phone the stylesheet drops
 * the map to 190px and moves it in to a 10px inset, and a guide label dodging
 * the box it used to occupy is dodging thin air while sitting squarely on the
 * real one. The constant stays as the fallback for the frame or two before
 * layout has happened, and for anything calling this outside a browser.
 */
function minimapBox(ctx, dpr) {
  const canvas = ctx.canvas;
  const fallback = () => {
    const right = canvas.width - MINIMAP.inset * dpr;
    return {
      left: right - MINIMAP.width * dpr,
      right,
      top: MINIMAP.inset * dpr,
      bottom: (MINIMAP.inset + MINIMAP.height) * dpr
    };
  };

  if (typeof canvas.closest !== 'function') return fallback();
  const stage = canvas.closest('.sg-tutorial-stage');
  const map = stage && stage.querySelector('.sg-minimap');
  if (!map) return fallback();

  const frame = canvas.getBoundingClientRect();
  if (!frame.width) return fallback();
  const box = map.getBoundingClientRect();
  // The backing store is not the CSS box, so CSS pixels have to be scaled up.
  const scale = canvas.width / frame.width;
  return {
    left: (box.left - frame.left) * scale,
    right: (box.right - frame.left) * scale,
    top: (box.top - frame.top) * scale,
    bottom: (box.bottom - frame.top) * scale
  };
}

function overlapsMinimap(ctx, dpr, box) {
  const map = minimapBox(ctx, dpr);
  return box.x1 > map.left && box.x0 < map.right && box.y1 > map.top && box.y0 < map.bottom;
}

/*
 * Break a line to fit a width, on spaces. A word that cannot fit on its own is
 * left to overhang rather than being broken mid-word — at these sizes that never
 * happens, and a hyphenless split reads as a typo.
 */
export function wrapToWidth(measure, text, limit) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (line && measure(next) > limit) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function label(ctx, text, at, dpr, color = ACCENT, offset = 16, dy = -10, align = 'left') {
  ctx.save();
  ctx.fillStyle = color;
  ctx.font = `600 ${13 * dpr}px system-ui, sans-serif`;

  /*
   * Flip to the other side of its mark rather than run under the map. The
   * southern latitude lesson is what needs this: it walks a marker with a long
   * label most of the way up the frame, and around 48 S the words start
   * disappearing. Flipping is better than moving the camera, which would mean
   * either a much wider field or a shorter sweep.
   */
  /*
   * `align` names the side of the mark the text sits on, so a right-aligned
   * label has to be offset leftward or it is drawn back across its own mark.
   */
  const baseline = at.y + dy * dpr;
  let side = align;
  let dx = side === 'right' ? -offset : offset;
  if (side === 'left') {
    const x0 = at.x + dx * dpr;
    const full = ctx.measureText(text).width;
    if (overlapsMinimap(ctx, dpr, { x0, x1: x0 + full, y0: baseline - 13 * dpr, y1: baseline + 4 * dpr })) {
      side = 'right';
      dx = -offset;
    }
  }

  /*
   * How much room this row actually has.
   *
   * On a desktop stage a guide label is a fraction of the width and none of this
   * matters. On a phone — and on a phone held sideways, where the stage is 481
   * points wide with a 190-point map in the corner — "Throughout the night, the
   * sky rotates around a fixed point" is simply wider than the gap beside the
   * map, and there is no side to flip it to that helps. So it wraps: the room is
   * whichever run of free space on this row is longer, and the block is grown
   * upward from the original baseline so a one-line label sits exactly where it
   * always did.
   */
  const margin = 6 * dpr;
  const map = minimapBox(ctx, dpr);
  const sharesRow = baseline + 4 * dpr > map.top && baseline - 13 * dpr < map.bottom;
  const room = sharesRow
    ? Math.max(map.left - margin * 2, ctx.canvas.width - map.right - margin * 2)
    : ctx.canvas.width - margin * 2;

  const lines = wrapToWidth((s) => ctx.measureText(s).width, text, Math.max(40 * dpr, room));
  const width = Math.max(...lines.map((line) => ctx.measureText(line).width));

  /*
   * Held inside the frame. Flipping a label solves one problem and can cause
   * another: a mark near the left edge, flipped away from the map, ends up drawn
   * from a negative x and the sentence begins off the side of the screen — "the
   * sky rotates around a fixed point" arrived as "y rotates around a fixed
   * point". Nudging it back in loosens the tie to its mark, which is a much
   * better trade than losing the first three words.
   */
  let x = at.x + dx * dpr;
  const [low, high] = side === 'right'
    ? [margin + width, ctx.canvas.width - margin]
    : [margin, ctx.canvas.width - margin - width];
  if (low <= high) x = Math.max(low, Math.min(high, x));

  ctx.textAlign = side;
  const lineHeight = 15 * dpr;
  lines.forEach((line, i) => {
    ctx.fillText(line, x, baseline - (lines.length - 1 - i) * lineHeight);
  });
  ctx.restore();
}

/*
 * A great-circle extension: step along the sky from `a` through `b` and keep
 * going. Drawn as a real arc rather than a straight screen line, because over
 * the tens of degrees these pointers span, the difference is visible and the
 * straight version would miss the pole.
 */
function extendArc(ctx, aVec, bVec, times, camera, dpr, color) {
  const points = [];
  const steps = 60;
  for (let i = 0; i <= steps; i += 1) {
    const f = (i / steps) * times;
    const x = aVec.east + (bVec.east - aVec.east) * f;
    const y = aVec.north + (bVec.north - aVec.north) * f;
    const z = aVec.up + (bVec.up - aVec.up) * f;
    const length = Math.hypot(x, y, z) || 1;
    const p = projectVector({ east: x / length, north: y / length, up: z / length }, camera);
    points.push(p);
  }

  ctx.save();
  ctx.setLineDash([7 * dpr, 7 * dpr]);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2 * dpr;
  ctx.beginPath();
  let started = false;
  for (const p of points) {
    if (!p) {
      started = false;
      continue;
    }
    if (started) ctx.lineTo(p.x, p.y);
    else ctx.moveTo(p.x, p.y);
    started = true;
  }
  ctx.stroke();
  ctx.restore();
}

const GUIDES = {
  // The point the whole sky turns around, marked whether or not a star sits on it.
  pole(ctx, camera, dpr, state) {
    /*
     * Altitude is the *absolute* latitude. Whichever pole is above your horizon
     * stands that many degrees up, due north of you in the northern hemisphere
     * and due south in the southern. Using the signed latitude put the marker
     * below the horizon everywhere south of the equator, which went unnoticed
     * while the only lesson drawing it stood at 45 N.
     */
    const pole = { alt: Math.abs(state.lat), az: state.lat >= 0 ? 0 : 180 };
    const at = toScreen(pole, camera);
    if (!at) return;
    ring(ctx, at, 9, dpr, WARM);
    ring(ctx, at, 16, dpr, 'rgba(255, 209, 128, 0.35)');
    label(ctx, 'Throughout the night, the sky rotates around a fixed point', at, dpr, WARM);
  },

  pointers(ctx, camera, dpr, state) {
    extendArc(ctx, toVector(STARS.merak, state), toVector(STARS.dubhe, state), 7.0, camera, dpr, ACCENT);

    const m = toScreen(place(STARS.merak, state), camera);
    const d = toScreen(place(STARS.dubhe, state), camera);
    if (m) ring(ctx, m, 7, dpr);
    if (d) ring(ctx, d, 7, dpr);
    if (m && d) {
      dashedLine(ctx, m, d, dpr, ACCENT);
      label(ctx, 'the pointers', m, dpr);
    }
  },

  polaris(ctx, camera, dpr, state) {
    const at = toScreen(place(STARS.polaris, state), camera);
    if (!at) return;
    ring(ctx, at, 10, dpr);
    ring(ctx, at, 17, dpr, FAINT);
    label(ctx, 'Polaris', at, dpr);
  },

  cross(ctx, camera, dpr, state) {
    extendArc(ctx, toVector(STARS.gacrux, state), toVector(STARS.acrux, state), 6.0, camera, dpr, ACCENT);

    const a = toScreen(place(STARS.acrux, state), camera);
    const g = toScreen(place(STARS.gacrux, state), camera);
    if (a) ring(ctx, a, 7, dpr);
    if (g) {
      ring(ctx, g, 7, dpr);
      label(ctx, 'the Southern Cross', g, dpr);
    }
  },

  southpole(ctx, camera, dpr, state) {
    const at = toScreen({ alt: -state.lat, az: 180 }, camera);
    if (!at) return;
    ring(ctx, at, 9, dpr, WARM);
    /*
     * Beside the marker on its left, reading into it.
     *
     * The Cross lesson draws its axis into this point from the upper right and
     * overshoots by a few degrees, so the arc's tail occupies everything below
     * and to the right. The previous placement dropped the words 58px straight
     * down into exactly that, which left them floating a marker-width away in
     * open sky, captioning nothing and lying along the very arrow that was
     * meant to be pointing at the marker. Left of the ring is the one quadrant
     * the arc never enters, and on the ring's own baseline the words read as its
     * label rather than as a stray line of text.
     */
    label(ctx, 'the celestial south pole has no star', at, dpr, WARM, 18, 5, 'right');
  },

  /*
   * The measurement itself: a line from the horizon straight up to the pole,
   * labelled with the angle. This is what a sextant reads, and the number it
   * shows is the latitude the caption claims.
   */
  altitude(ctx, camera, dpr, state) {
    const north = state.lat >= 0;
    const poleAlt = Math.abs(state.lat);
    const az = north ? 0 : 180;

    const top = toScreen({ alt: poleAlt, az }, camera);
    const foot = toScreen({ alt: 0, az }, camera);
    if (!top || !foot) return;

    ctx.save();
    ctx.strokeStyle = WARM;
    ctx.lineWidth = 2 * dpr;
    ctx.beginPath();
    ctx.moveTo(foot.x, foot.y);
    ctx.lineTo(top.x, top.y);
    ctx.stroke();

    ctx.setLineDash([4 * dpr, 5 * dpr]);
    ctx.lineWidth = 1.5 * dpr;
    ctx.beginPath();
    ctx.moveTo(foot.x - 70 * dpr, foot.y);
    ctx.lineTo(foot.x + 70 * dpr, foot.y);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.fillStyle = WARM;
    ctx.font = `700 ${17 * dpr}px system-ui, sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillText(
      `${poleAlt.toFixed(0)}°`,
      (foot.x + top.x) / 2 + 12 * dpr,
      (foot.y + top.y) / 2
    );
    ctx.restore();
  },

  /*
   * The circumpolar clock: the ring a star rides around the pole in a day, the
   * hours marked off around it, and the hand out to where that star has got to.
   *
   * The ring is not a dial drawn over the sky — it is the star's own path. A
   * star at a fixed declination circles the pole once a sidereal day at a
   * constant angular radius, so marking that circle every fifteen degrees is
   * marking it every hour, and the hand is the radius out to the star's real
   * position at this instant. That makes the reading checkable: if the astronomy
   * were wrong, the star would visibly sit off its own dial.
   */
  starclock(ctx, camera, dpr, state) {
    const star = handStar(state);
    const pole = toScreen(poleAt(state), camera);

    ctx.save();

    // The ring, clipped at the horizon. From every latitude the lessons use it
    // is wholly circumpolar, but the explorer can be dragged somewhere it is not.
    ctx.strokeStyle = FAINT;
    ctx.lineWidth = 1.5 * dpr;
    ctx.beginPath();
    let started = false;
    for (let hourAngle = 0; hourAngle <= 360; hourAngle += 2) {
      const at = dialPoint(star, state, hourAngle);
      const point = at.alt > 0 ? toScreen(at, camera) : null;
      if (!point) {
        started = false;
        continue;
      }
      if (started) ctx.lineTo(point.x, point.y);
      else ctx.moveTo(point.x, point.y);
      started = true;
    }
    ctx.stroke();

    /*
     * Twenty-four of them, because that is how many hours the turn takes — and
     * rotated by the dial's setting, so the mark the hand happens to be on is
     * the hour `starClockReading` says it is. Turning the marks rather than
     * relabelling them is what keeps whole hours on whole marks.
     */
    const mark = (hour) => dialMarkHourAngle(state, hour);
    for (let hour = 0; hour < 24; hour += 1) {
      const quarter = hour % 6 === 0;
      const inner = toScreen(dialPoint(star, state, mark(hour), quarter ? 0.82 : 0.92), camera);
      const outer = toScreen(dialPoint(star, state, mark(hour), 1.08), camera);
      if (!inner || !outer) continue;
      ctx.beginPath();
      ctx.strokeStyle = quarter ? ACCENT : FAINT;
      ctx.lineWidth = (quarter ? 2 : 1.2) * dpr;
      ctx.moveTo(inner.x, inner.y);
      ctx.lineTo(outer.x, outer.y);
      ctx.stroke();
    }

    // Numbered at the quarters only. Twenty-four numerals around a ring this
    // size is a smudge, and four is enough to see which way the hand is going.
    ctx.fillStyle = ACCENT;
    ctx.font = `600 ${12 * dpr}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const hour of [0, 6, 12, 18]) {
      const at = toScreen(dialPoint(star, state, mark(hour), 1.26), camera);
      if (at) ctx.fillText(`${hour}h`, at.x, at.y);
    }
    ctx.restore();

    const now = toScreen(place(star, state), camera);
    if (pole && now) {
      ctx.save();
      ctx.strokeStyle = WARM;
      ctx.lineWidth = 2.5 * dpr;
      ctx.beginPath();
      ctx.moveTo(pole.x, pole.y);
      ctx.lineTo(now.x, now.y);
      ctx.stroke();
      ctx.restore();
      ring(ctx, pole, 5, dpr, WARM);
      ring(ctx, now, 9, dpr, WARM);
      label(ctx, `the hand, ${starClockLabel(state)}h round`, now, dpr, WARM, 18, -16);
    }
  }
};

export function drawGuides(ctx, camera, dpr, state, names) {
  for (const name of names) {
    const guide = GUIDES[name];
    if (guide) guide(ctx, camera, dpr, state);
  }
}
